import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslatedSnackbar } from 'src/hooks/useTranslatedSnackbar';
import { organizationApi } from 'src/api/organization';
import { organizationAvailabilityApi } from 'src/api/organization-availability';
import Iconify from 'src/components/iconify/Iconify';
import { v4 as uuidv4 } from 'uuid';
import { GetOrganizationProfessionalsDto } from 'woofwoof-api';
import { useOrganizationContext } from '../../organization/hooks/useOrganizationContext';

// Add TimeOff interface to extend the API types
interface TimeOff {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  id?: string;
  professionalId?: string;
  professionalName?: string;
  description?: string;
}

// ----------------------------------------------------------------------

type TimeOffModalProps = {
  isOpen: boolean;
  onClose: () => void;
  timeOff: Object[];
  refetch: () => void;
  onSave: (
    timeOff: Object[],
    selectedProfessionalId?: string,
  ) => Promise<AxiosResponse<any>>;
  professionalId?: string;
  professionalName?: string;
  /** If true, shows professional selector for organization admins */
  showProfessionalSelector?: boolean;
  /** If true, this is for organization-wide time off (affects all professionals) */
  isOrganizationTimeOff?: boolean;
  /** If true, hides the existing periods list (e.g. when used on the calendar where events are visible) */
  hideExistingPeriods?: boolean;
};

type TimeOffPeriod = {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  id: string;
  professionalId?: string;
  professionalName?: string;
  description?: string;
};

export default function TimeOffModal({
  isOpen,
  onClose,
  timeOff,
  refetch,
  onSave,
  professionalId,
  professionalName,
  showProfessionalSelector = false,
  isOrganizationTimeOff = false,
  hideExistingPeriods = false,
}: TimeOffModalProps) {
  const { t, i18n } = useTranslation();
  const { enqueueTranslated } = useTranslatedSnackbar();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { selectedOrganization } = useOrganizationContext();
  const [timeOffPeriods, setTimeOffPeriods] = useState<TimeOffPeriod[]>([]);
  // Initialize with current date at 09:00
  const getDefaultStartDateTime = () => {
    const date = new Date();
    date.setHours(9, 0, 0, 0);
    return date;
  };
  // Initialize with current date at 17:00
  const getDefaultEndDateTime = () => {
    const date = new Date();
    date.setHours(17, 0, 0, 0);
    return date;
  };
  const [newStartDateTime, setNewStartDateTime] = useState<Date | null>(
    getDefaultStartDateTime(),
  );
  const [newEndDateTime, setNewEndDateTime] = useState<Date | null>(
    getDefaultEndDateTime(),
  );
  const [selectedProfessional, setSelectedProfessional] =
    useState<GetOrganizationProfessionalsDto | null>(null);
  const [isAddingTimeOff, setIsAddingTimeOff] = useState(false);
  const [description, setDescription] = useState('');

  // Fetch professionals for the organization (only when showProfessionalSelector is true)
  const { data: professionalsData } = useQuery(
    ['organization-professionals', selectedOrganization?.id],
    () =>
      organizationApi.organizationControllerGetOrganizationProfessionals({
        id: selectedOrganization?.id as string,
      }),
    {
      enabled: !!selectedOrganization?.id && showProfessionalSelector && isOpen,
    },
  );

  const professionals = useMemo(
    () => professionalsData?.data || [],
    [professionalsData?.data],
  );

  // Create a map of professionalId to professional name for quick lookup
  const professionalNameMap = useMemo(() => {
    const map = new Map<string, string>();
    professionals.forEach((prof) => {
      map.set(prof.id, `${prof.firstName} ${prof.lastName}`);
    });
    return map;
  }, [professionals]);

  // Fetch time off data for selected professional
  const {
    data: selectedProfessionalTimeOffData,
    refetch: refetchSelectedProfessionalTimeOff,
  } = useQuery(
    [
      'professional-time-off',
      selectedProfessional?.id,
      selectedOrganization?.id,
    ],
    () =>
      organizationAvailabilityApi.organizationAvailabilityControllerGetOrganizationAvailability(
        {
          organizationId: selectedOrganization?.id!,
          professionalId: selectedProfessional?.id!,
        },
      ),
    {
      enabled:
        !!selectedProfessional?.id &&
        !!selectedOrganization?.id &&
        showProfessionalSelector &&
        isOpen,
    },
  );

  // Determine which time off data to use based on mode and selection
  const getActiveTimeOff = () => {
    if (showProfessionalSelector && selectedProfessional) {
      return selectedProfessionalTimeOffData?.data?.timeOff || [];
    }
    return timeOff;
  };

  // Track if we've initialized for this modal session to avoid resetting on refetch
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // Reset initialization flag when modal closes
    if (!isOpen) {
      setHasInitialized(false);
      return;
    }

    // Only initialize when modal opens and we haven't initialized yet
    // or when selected professional changes in professional selector mode
    if (isOpen && !hasInitialized) {
      const dataToUse = getActiveTimeOff();
      // Note: No client-side filtering needed here because:
      // - For professionals: the backend endpoint getOrganizationTimeOff already returns only their own time off
      // - For admins: they select a specific professional, and the endpoint returns only that professional's time off
      const periodsWithIds = (dataToUse as TimeOff[]).map((period) => {
        const mappedPeriod: TimeOffPeriod = {
          ...period,
          id: period.id || uuidv4(),
        };

        // If professionalId exists and we have the name map, populate the name
        if (period.professionalId && professionalNameMap.size > 0) {
          mappedPeriod.professionalName = professionalNameMap.get(
            period.professionalId,
          );
        }

        return mappedPeriod;
      });
      setTimeOffPeriods(periodsWithIds);
      setNewStartDateTime(getDefaultStartDateTime());
      setNewEndDateTime(getDefaultEndDateTime());
      setDescription('');
      setHasInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    timeOff,
    selectedProfessionalTimeOffData?.data?.timeOff,
    showProfessionalSelector,
    professionalNameMap,
    professionalId,
  ]);

  // Re-initialize when selected professional changes
  useEffect(() => {
    if (isOpen && showProfessionalSelector && selectedProfessional) {
      setHasInitialized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showProfessionalSelector, selectedProfessional?.id]);

  // Auto-select first professional when data loads or modal opens
  useEffect(() => {
    if (
      isOpen &&
      showProfessionalSelector &&
      professionals.length > 0 &&
      !selectedProfessional
    ) {
      setSelectedProfessional(professionals[0]);
    }
  }, [isOpen, showProfessionalSelector, professionals, selectedProfessional]);

  // Reset selected professional when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedProfessional(null);
    }
  }, [isOpen]);

  // Helper to format time from Date
  const formatTime = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Check if a new time off period overlaps with existing periods
  const checkForOverlap = (startDateTime: Date, endDateTime: Date): boolean => {
    return timeOffPeriods.some((period) => {
      const periodStart = new Date(period.startDate);
      const periodEnd = new Date(period.endDate);

      // If period has specific times, use them
      if (period.startTime && period.endTime) {
        const [startH, startM] = period.startTime.split(':').map(Number);
        const [endH, endM] = period.endTime.split(':').map(Number);
        periodStart.setHours(startH, startM, 0, 0);
        periodEnd.setHours(endH, endM, 0, 0);
      }

      // Check if date-times overlap
      return startDateTime < periodEnd && endDateTime > periodStart;
    });
  };

  const handleAddTimeOff = async () => {
    // For professional selector mode, require a professional to be selected
    if (showProfessionalSelector && !selectedProfessional) {
      enqueueTranslated('snackbar.timeOffSelectProfessional', {
        variant: 'error',
      });
      return;
    }

    if (!newStartDateTime || !newEndDateTime) {
      enqueueTranslated('snackbar.timeOffSelectDates', {
        variant: 'error',
      });
      return;
    }

    if (newEndDateTime <= newStartDateTime) {
      enqueueTranslated('snackbar.timeOffEndAfterStart', {
        variant: 'error',
      });
      return;
    }

    // Validate minimum 10-minute duration
    const durationMs = newEndDateTime.getTime() - newStartDateTime.getTime();
    const durationMinutes = durationMs / (1000 * 60);
    if (durationMinutes < 10) {
      enqueueTranslated('snackbar.timeOffMinDuration', {
        variant: 'error',
      });
      return;
    }

    // Check for overlapping periods
    if (checkForOverlap(newStartDateTime, newEndDateTime)) {
      enqueueTranslated('snackbar.timeOffOverlap', {
        variant: 'error',
      });
      return;
    }

    const newTimeOff: TimeOffPeriod = {
      startDate: newStartDateTime.toISOString(),
      endDate: newEndDateTime.toISOString(),
      startTime: formatTime(newStartDateTime),
      endTime: formatTime(newEndDateTime),
      id: uuidv4(),
      ...(description.trim() && { description: description.trim() }),
      ...(showProfessionalSelector &&
        selectedProfessional && {
          professionalId: selectedProfessional.id,
          professionalName: `${selectedProfessional.firstName} ${selectedProfessional.lastName}`,
        }),
    };

    const updatedPeriods = [...timeOffPeriods, newTimeOff];

    setIsAddingTimeOff(true);
    try {
      const timeOffForBackend = updatedPeriods.map(
        ({
          startDate,
          endDate,
          startTime,
          endTime,
          professionalId,
          professionalName,
          description: desc,
        }) => ({
          startDate,
          endDate,
          ...(startTime && endTime && { startTime, endTime }),
          ...(professionalId && { professionalId }),
          ...(professionalName && { professionalName }),
          ...(desc && { description: desc }),
        }),
      );

      const targetProfessionalId = showProfessionalSelector
        ? selectedProfessional?.id
        : professionalId;

      const saveResult = await onSave(timeOffForBackend, targetProfessionalId);
      // User backed out at the schedule-conflict confirm modal — no change made.
      if ((saveResult as any)?.cancelled) {
        return;
      }
      setTimeOffPeriods(updatedPeriods);
      setNewStartDateTime(getDefaultStartDateTime());
      setNewEndDateTime(getDefaultEndDateTime());
      setDescription('');
      enqueueTranslated('snackbar.timeOffAdded', { variant: 'success' });

      if (hideExistingPeriods) {
        onClose();
        return;
      }

      // Refetch appropriate data
      if (showProfessionalSelector && selectedProfessional) {
        refetchSelectedProfessionalTimeOff();
      } else {
        refetch();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        String(t('snackbar.timeOffAddError'));
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsAddingTimeOff(false);
    }
  };

  const handleRemoveTimeOff = async (id: string) => {
    // Find the period being removed to get its professionalId
    const periodToRemove = timeOffPeriods.find((period) => period.id === id);

    // For professional selector mode, require a professional to be selected ONLY if the period doesn't have a professionalId
    if (
      showProfessionalSelector &&
      !selectedProfessional &&
      !periodToRemove?.professionalId
    ) {
      enqueueTranslated('snackbar.timeOffSelectProfessional', {
        variant: 'error',
      });
      return;
    }

    const updatedPeriods = timeOffPeriods.filter((period) => period.id !== id);

    try {
      const timeOffForBackend = updatedPeriods.map(
        ({
          startDate,
          endDate,
          startTime,
          endTime,
          professionalId,
          professionalName,
          description: desc,
        }) => ({
          startDate,
          endDate,
          ...(startTime && endTime && { startTime, endTime }),
          ...(professionalId && { professionalId }),
          ...(professionalName && { professionalName }),
          ...(desc && { description: desc }),
        }),
      );

      // Use professionalId from the period if available, otherwise use selected professional or prop
      const targetProfessionalId =
        periodToRemove?.professionalId ||
        (showProfessionalSelector ? selectedProfessional?.id : professionalId);

      const saveResult = await onSave(timeOffForBackend, targetProfessionalId);
      if ((saveResult as any)?.cancelled) {
        return;
      }
      setTimeOffPeriods(updatedPeriods);
      enqueueTranslated('snackbar.timeOffRemoved', { variant: 'success' });

      // Refetch appropriate data
      if (
        showProfessionalSelector &&
        (selectedProfessional || periodToRemove?.professionalId)
      ) {
        if (selectedProfessional) {
          refetchSelectedProfessionalTimeOff();
        } else {
          refetch();
        }
      } else {
        refetch();
      }
    } catch (error) {
      enqueueTranslated('snackbar.timeOffRemoveError', { variant: 'error' });
    }
  };

  const dateLocale = i18n.language.startsWith('pl') ? 'pl-PL' : 'en-US';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(dateLocale);
  };

  const formatTimeOffPeriod = (period: TimeOffPeriod) => {
    const startDate = formatDate(period.startDate);
    const endDate = formatDate(period.endDate);

    if (period.startTime && period.endTime) {
      return `${startDate} ${period.startTime} - ${endDate} ${period.endTime}`;
    }

    return `${startDate} - ${endDate}`;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
            m: 0,
          },
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Iconify icon="mdi:calendar-remove" width={24} height={24} />
          <Typography variant="h6">
            {isOrganizationTimeOff
              ? t('visit.timeOffOrgTitle')
              : showProfessionalSelector
              ? t('visit.timeOffStaffTitle')
              : t('visit.timeOffProTitle')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Professional selector for organization admins */}
          {showProfessionalSelector && (
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>{t('visit.timeOffSpecialistField')}</InputLabel>
                <Select
                  label={String(t('visit.timeOffSpecialistField'))}
                  value={selectedProfessional?.id || ''}
                  onChange={(e) => {
                    const prof = professionals.find(
                      (p) => p.id === e.target.value,
                    );
                    setSelectedProfessional(prof || null);
                  }}
                >
                  {professionals.map((prof) => (
                    <MenuItem key={prof.id} value={prof.id}>
                      {prof.firstName} {prof.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Organization time off info */}
          {isOrganizationTimeOff && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.info.lighter,
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="info.dark">
                  {t('visit.timeOffOrgHint')}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Regular time off info */}
          {!isOrganizationTimeOff && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.info.lighter,
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="info.dark">
                  {t('visit.timeOffProHint')}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {t('visit.timeOffAddPeriod')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'nowrap',
              }}
            >
              <MobileDateTimePicker
                label={String(t('calendar.schedule.timeFrom'))}
                value={newStartDateTime}
                onChange={(newValue: any) => {
                  setNewStartDateTime(newValue);
                  if (newValue && newEndDateTime && newValue > newEndDateTime) {
                    const newEnd = new Date(newValue);
                    newEnd.setHours(newEnd.getHours() + 1);
                    setNewEndDateTime(newEnd);
                  }
                }}
                minutesStep={5}
                ampm={false}
                disabled={isAddingTimeOff}
                renderInput={(params) => (
                  <TextField {...params} fullWidth sx={{ flex: 1 }} />
                )}
              />
              <Typography
                sx={{
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              >
                -
              </Typography>
              <MobileDateTimePicker
                label={String(t('calendar.schedule.timeTo'))}
                value={newEndDateTime}
                minDate={newStartDateTime || undefined}
                onChange={(newValue: any) => setNewEndDateTime(newValue)}
                minutesStep={5}
                ampm={false}
                disabled={isAddingTimeOff}
                renderInput={(params) => (
                  <TextField {...params} fullWidth sx={{ flex: 1 }} />
                )}
              />
            </Box>
            {!isOrganizationTimeOff && (
              <TextField
                label={String(t('visit.timeOffReasonLabel'))}
                placeholder={String(t('visit.timeOffReasonPlaceholder'))}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
                disabled={isAddingTimeOff}
                sx={{ mt: 2 }}
              />
            )}
            {!hideExistingPeriods && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <LoadingButton
                  variant="contained"
                  loading={isAddingTimeOff}
                  onClick={handleAddTimeOff}
                  startIcon={<Iconify icon="eva:plus-fill" width={18} />}
                >
                  {t('common.add')}
                </LoadingButton>
              </Box>
            )}
          </Grid>

          {timeOffPeriods.length > 0 && !hideExistingPeriods && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('visit.timeOffPeriodsHeading')}
              </Typography>
              {timeOffPeriods.map((period) => (
                <Box
                  key={period.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    mb: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography>{formatTimeOffPeriod(period)}</Typography>
                    {period.startTime && period.endTime && (
                      <Typography variant="caption" color="text.secondary">
                        {t('visit.timeOffHoursRange', {
                          start: period.startTime,
                          end: period.endTime,
                        })}
                      </Typography>
                    )}
                    {period.professionalName && (
                      <Typography
                        variant="caption"
                        color="primary.main"
                        sx={{ display: 'block' }}
                      >
                        {t('visit.timeOffSpecialistLine', {
                          name: period.professionalName,
                        })}
                      </Typography>
                    )}
                    {period.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {period.description}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    color="error"
                    data-testid="timeoff-period-delete"
                    onClick={() => handleRemoveTimeOff(period.id)}
                  >
                    <Iconify
                      icon="eva:trash-2-outline"
                      width={20}
                      height={20}
                    />
                  </IconButton>
                </Box>
              ))}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        sx={hideExistingPeriods ? { justifyContent: 'space-between' } : {}}
      >
        <Button variant="outlined" color="inherit" onClick={onClose}>
          {t('quickBooking.close')}
        </Button>
        {hideExistingPeriods && (
          <LoadingButton
            variant="contained"
            data-testid="timeoff-add"
            loading={isAddingTimeOff}
            onClick={handleAddTimeOff}
          >
            {t('common.add')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
